import * as React from 'react'
import { useCallback, useMemo } from 'react'

import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import { getVisibleLanguageOptions } from 'i18n/util/locale'
import { LabRemoteConfigKeys } from 'constants/lab'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { syncUserDetails } from 'actions/AuthActions'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'

interface Props {
  onLanguageSelect?: (e: React.MouseEvent<HTMLButtonElement>, changed: boolean) => void
}

function NavBarLanguageItems(props: Props) {
  const { onLanguageSelect } = props
  const dispatch = useAppDispatch()
  const locale = useAppSelector((state) => state.geo.locale)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(() => {
    return getVisibleLanguageOptions(languagesRemoteConfig)
  }, [languagesRemoteConfig])

  const currentLanguage =
    languageOptions.find((lang) => lang.locale === locale) ??
    languageOptions[0]

  const onLanguageClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      const selectedLanguage = languageOptions.find(
        (language) => language.locale === e.currentTarget.dataset.locale,
      )
      if (selectedLanguage) {
        const changed = selectedLanguage.locale !== currentLanguage?.locale
        if (changed) {
          setLanguageCookie(selectedLanguage.locale)
          if (isLoggedIn) {
            updateCustomerLanguagePreferenceFromAuth(selectedLanguage.locale)
              .catch(reportClientError)
            dispatch(syncUserDetails({ language: selectedLanguage.locale }))
          }
          const currentLocationUrl = UrlLocation.fromLocation(window.location)
          const redirect = currentLocationUrl.getRedirectToLocale(
            selectedLanguage.locale,
            languageOptions,
          )
          const targetUrl = currentLocationUrl.withRedirect(redirect)
          if (!targetUrl.equals(currentLocationUrl)) {
            window.location.href = targetUrl.toString()
          }
        }
        onLanguageSelect?.(e, changed)
      }
    },
    [dispatch, currentLanguage?.locale, languageOptions, onLanguageSelect, isLoggedIn],
  )

  return (
    <>
      {languageOptions.map((language) => (
        <ClickableListItem
          size="medium"
          key={language.locale}
          endIcon={
            currentLanguage?.locale === language.locale ? (
              <LineCheckIcon />
            ) : undefined
          }
          title={language.nativeName}
          data-locale={language.locale}
          onClick={onLanguageClick}
          selected={currentLanguage?.locale === language.locale}
        />
      ))}
    </>
  )
}

export default React.memo(NavBarLanguageItems)
